import { useMemo, useState } from "react"
import type { CombinedError } from "urql"
import { useQuery } from "urql"

import { formatGlAccountOptionLabel } from "../utils/formatGlAccountOptionLabel"

import { AKROCHEM_COMPANY_NAME } from "@/constants/common"
import {
  GetVendorDefaultExpenseAccountOptionsDocument,
  type GlAccount,
  type GlAccountQueryListArgs,
  GlAccountType,
  type Query,
} from "@/graphql/codegen/graphql"

interface DefaultExpenseAccountOption {
  id: string
  label: string
}

interface UseDefaultExpenseAccountOptionsReturn {
  glAccounts: DefaultExpenseAccountOption[]
  isFetching: boolean
  error?: CombinedError
  setSearchTerm: (term: string) => void
}

const EXPENSE_ACCOUNT_OPTIONS_LIMIT = 1000

const transformToOption = (glAccount: GlAccount): DefaultExpenseAccountOption => ({
  id: glAccount.glAccountId ?? "",
  label: formatGlAccountOptionLabel(
    { number: glAccount.number, subNumber: glAccount.subNumber, title: glAccount.title },
    !glAccount.isActive
  ),
})

export const useDefaultExpenseAccountOptions = (): UseDefaultExpenseAccountOptionsReturn => {
  const [searchTerm, setSearchTerm] = useState("")

  const [{ data, fetching, error }] = useQuery<Pick<Query, "glAccount">, GlAccountQueryListArgs>({
    query: GetVendorDefaultExpenseAccountOptionsDocument,
    variables: {
      input: {
        filter: {
          type: [GlAccountType.Expense],
          isActive: true,
          companyName: AKROCHEM_COMPANY_NAME,
          query: searchTerm || undefined,
        },
        offset: 0,
        limit: EXPENSE_ACCOUNT_OPTIONS_LIMIT,
      },
    },
  })

  const glAccounts = useMemo<DefaultExpenseAccountOption[]>(() => {
    if (data?.glAccount.list.__typename !== "GlAccountListSuccess") return []

    return data.glAccount.list.glAccounts
      .filter((glAccount) => glAccount !== null)
      .map(transformToOption)
      .sort((a, b) => a.label.localeCompare(b.label))
  }, [data])

  return {
    glAccounts,
    isFetching: fetching,
    error,
    setSearchTerm,
  }
}
